import { shuffle } from "./format";
import type { SrsRecord } from "./srs";

/**
 * Ordering for drill rounds. A round of 10 out of 40 words should not be a
 * blind shuffle: the words the learner is actually failing come first.
 *
 * Pure, so the priority rules can be tested without a store — the schedule
 * lookup is passed in.
 */

/** Lower tier = drilled sooner. */
export const Tier = {
  /** Scheduled and overdue: the words actively slipping. */
  Due: 0,
  /** Never answered: new material. */
  New: 1,
  /** Answered recently, still in the early boxes. */
  Learning: 2,
  /** Mastered and parked on a long interval. */
  Mastered: 3,
} as const;

export type Tier = (typeof Tier)[keyof typeof Tier];

export function tierFor(record: SrsRecord | undefined, now: number): Tier {
  if (!record) return Tier.New;
  if (record.due <= now) return Tier.Due;
  return record.box <= 2 ? Tier.Learning : Tier.Mastered;
}

/**
 * Sort into priority tiers, shuffling inside each one so repeated rounds
 * don't ask the same questions in the same order.
 */
export function orderByPriority<T>(
  items: readonly T[],
  recordOf: (item: T) => SrsRecord | undefined,
  now: number
): T[] {
  const buckets: T[][] = [[], [], [], []];
  for (const item of items) buckets[tierFor(recordOf(item), now)].push(item);
  return buckets.flatMap((bucket) => shuffle(bucket));
}
